/**
 * The gunsmith's state: what is fitted to what, what has been earned, and what the
 * resulting weapon actually is.
 *
 * One instance, created by `WeaponSystem`, published as `ctx.attachments`. Three
 * jobs, kept apart:
 *
 *  - **Builds.** One attachment per slot per weapon, enforced by the data structure
 *    (a slot is a key) and by `equip`, which refuses an incompatible part, a locked
 *    part, or a part that would take the build past its point limit. The gunsmith
 *    screen is one caller of that method, not the place the rules live. A rule that
 *    only exists in a screen is a rule that stops existing the moment anything else
 *    — a saved file, a console, a second screen — touches the same state.
 *
 *  - **Progress.** Per-weapon XP and the level derived from it, plus the shared set
 *    of optic unlocks. Persisted under its own key, entirely separate from career
 *    progression, which this module never reads or writes.
 *
 *  - **Resolution.** Every build resolves through `ResolveCache`, so the effective
 *    weapon is computed on change and not per shot, and so `ViewModel` can hold a
 *    reference to the resolved object across frames.
 *
 * Emits: `attachments:set { weapon, slots, points, limit, def }`
 *        `weapon:xp     { weapon, amount, reason, total, level }`
 *        `weapon:level  { weapon, level, unlocked[] }`
 * Listens: `hit:actor`, `actor:death`
 */

import { WEAPONS } from '../weapons.data.js';
import { SLOT_ORDER } from './schema.js';
import { CATALOG, auditCatalog } from './catalog.js';
import { MASTERY, MASTERY_BY_WEAPON } from './mastery.js';
import { fits, slotListFor, opticGroupFor } from './compatibility.js';
import { ResolveCache, resolveInto } from './resolver.js';
import {
  TUNING as UNLOCK_TUNING,
  LADDERS,
  weaponLevelForXp,
  weaponXpToReach,
  weaponXpForLevel,
  unlocksAtWeaponLevel,
  opticUnlockKey,
  opticKeysEarned,
} from './unlocks.js';

export const TUNING = {
  /** Build points available with no expansion earned. */
  baseAttachmentLimit: 5,
  /** Build points available once the expansion condition is met. */
  expandedAttachmentLimit: 8,

  saveKey: 'blackout.gunsmith',
  saveVersion: 1,
  saveDebounce: 0.75,
};

/** Every attachment in the game, ordinary and mastery, keyed by id. */
export const ALL_ATTACHMENTS = { ...CATALOG, ...MASTERY };

/**
 * The no-free-lunch audit, run at module load.
 *
 * A warning rather than a throw: a balance defect must not be able to stop the game
 * booting, and the report is what the next person to touch the catalog needs to see.
 */
{
  const bad = auditCatalog(ALL_ATTACHMENTS);
  if (bad.length) {
    console.warn(
      '[attachments] parts with no genuine downside:',
      bad.map((b) => `${b.id} (${b.why})`).join(', ')
    );
  }
}

/**
 * Whether a player has earned the expanded point limit.
 *
 * Deliberately a swappable function with a deliberately boring default. The
 * condition is a design decision that has not been made yet — a wildcard unlock, a
 * prestige, a per-weapon mastery, a match modifier — and every one of those wants a
 * different signal. What must not happen in the meantime is the condition being
 * inlined into `limitFor` in five places, so it lives here behind one seam.
 *
 * @param state { weaponId, level, masteryEquipped, loadouts }
 */
let expansionCondition = () => false;

/** Replace the expanded-limit condition. See `expansionCondition`. */
export function setExpansionCondition(fn) {
  expansionCondition = typeof fn === 'function' ? fn : () => false;
}

/* -------------------------------------------------------------------------- */

export class WeaponLoadouts {
  constructor(ctx) {
    this.ctx = ctx;
    this.events = ctx?.events ?? null;

    /** weapon id -> { slot: attachmentId }. One attachment per slot, by construction. */
    this.builds = new Map();
    /** weapon id -> lifetime weapon XP. Level is always derived from this. */
    this.xp = new Map();
    /** `${mountGroup}:${opticId}` for every optic earned anywhere in that group. */
    this.optics = new Set();

    this.cache = new ResolveCache();
    this._offs = [];
    this._lastDamager = new WeakMap();
    this._multi = new Map();
    this._saveIn = -1;

    this._load();
    this._bind();
    if (ctx?.engine) ctx.engine.registerUpdate((dt) => this._tickSave(dt), 'ui');
  }

  /* ------------------------------------------------------------------ */
  /* queries                                                             */
  /* ------------------------------------------------------------------ */

  /** The build for a weapon as `{ slot: attachmentId }`. Never null. */
  build(weaponId) {
    let b = this.builds.get(weaponId);
    if (!b) {
      b = {};
      this.builds.set(weaponId, b);
    }
    return b;
  }

  /** The fitted attachment entries, ordered by slot. This is what the resolver eats. */
  fitted(weaponId) {
    const b = this.builds.get(weaponId);
    if (!b) return [];
    const out = [];
    for (const slot of SLOT_ORDER) {
      const id = b[slot];
      const a = id && ALL_ATTACHMENTS[id];
      if (a) out.push(a);
    }
    return out;
  }

  /** Build points spent. */
  points(weaponId) {
    return this.fitted(weaponId).reduce((n, a) => n + (a.cost ?? 1), 0);
  }

  /**
   * Build points available to this weapon right now.
   *
   * A build can legitimately sit *over* its limit: if the expansion condition is
   * later revoked, an eight-point build stays fitted and `equip` simply refuses to
   * add to it. That is the deliberate choice — silently stripping parts off a saved
   * build to satisfy a rule that changed under the player is worse than briefly
   * allowing a build the gunsmith would not let them assemble today. The screen
   * should show `points > limit` rather than pretend it cannot happen.
   */
  limitFor(weaponId) {
    const expanded = !!expansionCondition({
      weaponId,
      level: this.level(weaponId),
      masteryEquipped: this.hasMastery(weaponId),
      loadouts: this,
    });
    return expanded ? TUNING.expandedAttachmentLimit : TUNING.baseAttachmentLimit;
  }

  hasMastery(weaponId) {
    const m = MASTERY_BY_WEAPON[weaponId];
    return !!m && this.fitted(weaponId).some((a) => a.id === m);
  }

  /** Weapon level, derived from weapon XP. Never stored. */
  level(weaponId) {
    return weaponLevelForXp(this.xp.get(weaponId) ?? 0);
  }

  /** Progress within the current weapon level, for a bar. */
  levelProgress(weaponId) {
    const xp = this.xp.get(weaponId) ?? 0;
    const level = weaponLevelForXp(xp);
    const floor = weaponXpToReach(level);
    const cost = weaponXpForLevel(level);
    return {
      level,
      xp,
      into: xp - floor,
      cost,
      frac: cost > 0 ? Math.min(1, (xp - floor) / cost) : 1,
      max: level >= UNLOCK_TUNING.maxWeaponLevel,
    };
  }

  /**
   * Has this weapon earned this part?
   *
   * Optics answer from the shared set: a sight earned on any weapon in the same
   * mount group is available here immediately, whatever this weapon's own level is.
   * Everything else is this weapon's level against this weapon's ladder.
   */
  isUnlocked(weaponId, attId) {
    const a = ALL_ATTACHMENTS[attId];
    if (!a) return false;
    if (!fits(a, weaponId)) return false;
    const need = LADDERS[weaponId]?.get(attId);
    if (need == null) return false;
    if (a.slot === 'optic' && this.optics.has(opticUnlockKey(weaponId, attId))) return true;
    return this.level(weaponId) >= need;
  }

  /** Every part that fits this weapon, with its ladder level and whether it is earned. */
  catalogFor(weaponId) {
    const ladder = LADDERS[weaponId];
    if (!ladder) return [];
    const out = [];
    for (const [attId, level] of ladder) {
      const a = ALL_ATTACHMENTS[attId];
      out.push({
        id: attId,
        slot: a.slot,
        name: a.name,
        cost: a.cost ?? 1,
        mastery: !!a.mastery,
        unlockLevel: level,
        unlocked: this.isUnlocked(weaponId, attId),
        pro: a.pro,
        con: a.con,
        blurb: a.blurb,
      });
    }
    return out;
  }

  /** The slots this weapon physically has, in order. */
  slots(weaponId) {
    return slotListFor(weaponId);
  }

  /** The effective weapon definition for this build. Cached; safe to hold. */
  effective(weaponId) {
    return this.cache.get(weaponId, this.fitted(weaponId));
  }

  /**
   * Resolve a hypothetical build without touching what the player has fitted.
   *
   * The Gunsmith needs this to answer "what would this part do?" and it must not
   * go through `effective()`: the cache hands back one reused object per weapon,
   * and `ViewModel` holds that object across frames — resolving a preview into it
   * would make the gun the player is holding briefly become the gun they are
   * merely hovering over.
   *
   * @param {string} weaponId
   * @param {string[]} attachmentIds
   * @param {object} [out] optional target, so a caller polling on cursor movement
   *   can reuse one object instead of allocating per keystroke.
   */
  preview(weaponId, attachmentIds, out = {}) {
    // `resolveInto` takes attachment *objects* — it reads `.mods`, `.slot` and
    // `.cost` off each one. Ids are what every caller has, so the lookup belongs
    // here. Unknown ids are dropped rather than throwing: a preview is a question,
    // and the honest answer to "what would this unknown part do" is "nothing".
    const list = [];
    for (const id of attachmentIds ?? []) {
      const a = ALL_ATTACHMENTS[id];
      if (a) list.push(a);
    }
    return resolveInto(weaponId, list, out);
  }

  /* ------------------------------------------------------------------ */
  /* mutation                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Fit a part.
   *
   * Every rejection carries a reason, because the gunsmith screen has to say *why*
   * and because a silent no-op is the hardest kind of bug to see from the outside.
   *
   * @param opts.replace  default true. A slot holds one part; fitting a second
   *                      replaces the first. Pass `false` to be told the slot is
   *                      occupied instead, which is what a drag-and-drop UI wants.
   * @param opts.force    skip the unlock check only. Compatibility, slot and point
   *                      rules still apply — a "force" that could produce an
   *                      impossible build would make every downstream assumption
   *                      about builds untrue.
   * @returns { ok, reason?, replaced?, points, limit }
   */
  equip(weaponId, attId, opts = {}) {
    const replace = opts.replace !== false;
    const def = WEAPONS[weaponId];
    if (!def) return this._fail(weaponId, 'no-such-weapon');
    const a = ALL_ATTACHMENTS[attId];
    if (!a) return this._fail(weaponId, 'no-such-attachment');
    if (!fits(a, weaponId)) return this._fail(weaponId, 'incompatible');

    const b = this.build(weaponId);
    const held = b[a.slot];
    if (held === attId) {
      return { ok: true, reason: 'already-fitted', points: this.points(weaponId), limit: this.limitFor(weaponId) };
    }
    if (held && !replace) return this._fail(weaponId, 'slot-occupied');

    if (!opts.force && !this.isUnlocked(weaponId, attId)) {
      // Mastery is the only lock a player can see coming from a long way off, so it
      // is worth a reason of its own rather than a generic "locked".
      return this._fail(weaponId, a.mastery ? 'locked-mastery' : 'locked');
    }

    const heldCost = held ? ALL_ATTACHMENTS[held]?.cost ?? 1 : 0;
    const limit = this.limitFor(weaponId);
    const after = this.points(weaponId) - heldCost + (a.cost ?? 1);
    if (after > limit) return this._fail(weaponId, 'points');

    b[a.slot] = attId;
    this._changed(weaponId);
    return { ok: true, replaced: held ?? null, points: this.points(weaponId), limit };
  }

  /** Remove whatever is in a slot. */
  unequip(weaponId, slot) {
    const b = this.builds.get(weaponId);
    if (!b || !b[slot]) return { ok: false, reason: 'empty-slot' };
    const was = b[slot];
    delete b[slot];
    this._changed(weaponId);
    return { ok: true, removed: was, points: this.points(weaponId), limit: this.limitFor(weaponId) };
  }

  /** Strip a weapon back to stock. */
  clear(weaponId) {
    this.builds.set(weaponId, {});
    this._changed(weaponId);
    return { ok: true, points: 0, limit: this.limitFor(weaponId) };
  }

  _fail(weaponId, reason) {
    return { ok: false, reason, points: this.points(weaponId), limit: this.limitFor(weaponId) };
  }

  _changed(weaponId) {
    this.cache.invalidate(weaponId);
    const def = this.effective(weaponId);
    this._queueSave();
    this.events?.emit('attachments:set', {
      weapon: weaponId,
      slots: { ...this.build(weaponId) },
      points: this.points(weaponId),
      limit: this.limitFor(weaponId),
      def,
    });
  }

  /* ------------------------------------------------------------------ */
  /* progress                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Award weapon XP, and level the weapon up if it crosses a threshold.
   *
   * Level is derived from XP on every read, so there is exactly one number that can
   * be corrupted and no way for level and XP to disagree — the same shape the career
   * ladder uses, for the same reason.
   */
  addXp(weaponId, amount, reason = 'kill') {
    if (!WEAPONS[weaponId] || !(amount > 0)) return 0;
    const before = this.level(weaponId);
    const total = (this.xp.get(weaponId) ?? 0) + amount;
    this.xp.set(weaponId, total);
    const after = weaponLevelForXp(total);

    this.events?.emit('weapon:xp', { weapon: weaponId, amount, reason, total, level: after });

    if (after > before) {
      for (let L = before + 1; L <= after; L++) {
        const unlocked = unlocksAtWeaponLevel(weaponId, L);
        // An optic earned here is earned for every weapon sharing the mount, right
        // now and permanently. Recorded before the event so a listener that reads
        // `isUnlocked` during the banner gets the right answer.
        for (const id of unlocked) {
          if (ALL_ATTACHMENTS[id]?.slot === 'optic') this.optics.add(opticUnlockKey(weaponId, id));
        }
        this.events?.emit('weapon:level', { weapon: weaponId, level: L, unlocked });
      }
      this._queueSave();
    }
    this._queueSave();
    return amount;
  }

  /** Set a weapon's level outright. For the test harness and for debug tooling. */
  setLevel(weaponId, level) {
    if (!WEAPONS[weaponId]) return false;
    const L = Math.min(UNLOCK_TUNING.maxWeaponLevel, Math.max(1, Math.floor(level)));
    this.xp.set(weaponId, weaponXpToReach(L));
    for (const key of opticKeysEarned(weaponId, L)) this.optics.add(key);
    this._queueSave();
    return true;
  }

  /** Unlock everything on every weapon. Test harness only. */
  unlockAll() {
    for (const id of Object.keys(WEAPONS)) this.setLevel(id, UNLOCK_TUNING.maxWeaponLevel);
    return true;
  }

  _bind() {
    const ev = this.events;
    if (!ev) return;

    // `hit:actor` carries a weapon id only when it came from `Ballistics`, i.e. from
    // the player's weapon. The enemies' own hit events carry no weapon and are
    // filtered out by that alone; the player check is belt and braces.
    this._offs.push(
      ev.on('hit:actor', (p) => {
        if (!p?.weapon || !p.actor) return;
        if (p.actor === this.ctx?.player || p.actor?.id === 'player') return;
        this._lastDamager.set(p.actor, p.weapon);
        this.addXp(p.weapon, UNLOCK_TUNING.xpHit, 'hit');
      })
    );

    this._offs.push(
      ev.on('actor:death', (p) => {
        if (!p?.actor || !p.mine) return;
        const weaponId = this._lastDamager.get(p.actor);
        if (!weaponId) return;
        this._lastDamager.delete(p.actor);
        this.addXp(weaponId, UNLOCK_TUNING.xpKill, 'kill');
        if (p.headshot) this.addXp(weaponId, UNLOCK_TUNING.xpHeadshot, 'headshot');

        // Multi-kills escalate on the weapon that got them, on the weapon's own
        // window. Independent of the career ladder's multi-kill bonus by design:
        // switching weapons mid-spree should break the weapon's streak and not the
        // player's.
        const now = this.ctx?.engine?.elapsed ?? performance.now() / 1000;
        const m = this._multi.get(weaponId);
        const chain = m && now - m.at <= UNLOCK_TUNING.multiWindow ? m.n + 1 : 1;
        this._multi.set(weaponId, { n: chain, at: now });
        if (chain > 1) {
          const bonus = Math.min(UNLOCK_TUNING.xpMultiMax, UNLOCK_TUNING.xpMultiStep * (chain - 1));
          this.addXp(weaponId, bonus, 'multi');
        }
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /* persistence                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Save shape, and what happens when it is wrong.
   *
   * A save that fails to parse, is from an older version, or contains a weapon or an
   * attachment that no longer exists is discarded field by field rather than
   * wholesale — an attachment removed from the catalog should cost the player that
   * one part, not their entire gunsmith. Never throws; a broken save is a fresh
   * start, not a broken game.
   */
  _load() {
    let raw = null;
    try {
      raw = globalThis.localStorage?.getItem(TUNING.saveKey);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (!data || data.v !== TUNING.saveVersion) return;
      for (const [id, xp] of Object.entries(data.xp ?? {})) {
        if (WEAPONS[id] && Number.isFinite(xp) && xp >= 0) this.xp.set(id, xp);
      }
      for (const [id, slots] of Object.entries(data.builds ?? {})) {
        if (!WEAPONS[id] || !slots || typeof slots !== 'object') continue;
        const b = this.build(id);
        for (const [slot, attId] of Object.entries(slots)) {
          const a = ALL_ATTACHMENTS[attId];
          if (a && a.slot === slot && fits(a, id)) b[slot] = attId;
        }
      }
      for (const key of data.optics ?? []) if (typeof key === 'string') this.optics.add(key);
      // Anything the ladder says this weapon has already earned in the optic slot is
      // re-derived, so a save written before an optic existed still hands it over.
      for (const id of this.xp.keys()) {
        for (const key of opticKeysEarned(id, this.level(id))) this.optics.add(key);
      }
    } catch (err) {
      console.warn('[attachments] discarding an unreadable gunsmith save', err);
    }
  }

  _queueSave() {
    if (this._saveIn < 0) this._saveIn = TUNING.saveDebounce;
  }

  _tickSave(dt) {
    if (this._saveIn < 0) return;
    this._saveIn -= dt;
    if (this._saveIn > 0) return;
    this._saveIn = -1;
    this.save();
  }

  save() {
    try {
      const builds = {};
      for (const [id, slots] of this.builds) if (Object.keys(slots).length) builds[id] = slots;
      globalThis.localStorage?.setItem(
        TUNING.saveKey,
        JSON.stringify({
          v: TUNING.saveVersion,
          xp: Object.fromEntries(this.xp),
          builds,
          optics: [...this.optics],
        })
      );
    } catch {
      // Storage full, disabled, or a private window. Losing a save is not worth a
      // crash, and there is nothing useful to do about it here.
    }
  }

  reset() {
    this.builds.clear();
    this.xp.clear();
    this.optics.clear();
    this.cache.invalidate();
    try {
      globalThis.localStorage?.removeItem(TUNING.saveKey);
    } catch {
      /* nothing to do */
    }
  }

  dispose() {
    for (const off of this._offs) off?.();
    this._offs.length = 0;
    this.save();
  }

  /* ------------------------------------------------------------------ */

  /** Everything about one weapon, in one object. For the gunsmith screen. */
  summary(weaponId) {
    return {
      weapon: weaponId,
      name: WEAPONS[weaponId]?.name,
      slots: this.slots(weaponId),
      build: { ...this.build(weaponId) },
      points: this.points(weaponId),
      limit: this.limitFor(weaponId),
      ...this.levelProgress(weaponId),
      mastery: MASTERY_BY_WEAPON[weaponId] ?? null,
      opticGroup: opticGroupFor(weaponId),
    };
  }
}
